@if (isVisible()) {
  <div class="sce-back">
    <div class="sce-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true" aria-labelledby="sceTitle">
      <div class="sce-head">
        <h3 id="sceTitle">
          Send Estimate to Customer
          @if (estimateLabel()) { <span class="sce-head__sub">{{ estimateLabel() }}</span> }
        </h3>
        <button type="button" class="sce-x" (click)="cancel()" aria-label="Close">✕</button>
      </div>

      <div class="sce-body">
        @if (isLoading()) {
          <div class="sce-status">Loading contacts…</div>
        } @else {
          @if (recipientsError()) {
            <div class="sce-alert sce-alert--error">{{ recipientsError() }}</div>
          }
          @if (errorMessage()) {
            <div class="sce-alert sce-alert--error">{{ errorMessage() }}</div>
          }

          @if (accountContacts().length) {
            <div class="sce-group">
              <div class="sce-group__head">Account contacts</div>
              @for (r of accountContacts(); track r.contactKey) {
                <label class="sce-item" [class.sce-item--disabled]="!r.email">
                  <input
                    type="checkbox"
                    [checked]="isRecipientSelected(r)"
                    [disabled]="!r.email || isSending()"
                    (change)="toggleRecipient(r)" />
                  <span class="sce-item__name">
                    {{ r.name || 'Unnamed contact' }}
                    @if (r.title) { <span class="sce-item__title">— {{ r.title }}</span> }
                  </span>
                  <span class="sce-item__email">{{ r.email || 'No email on file' }}</span>
                  @if (r.isJobDefaultContact) { <span class="sce-item__badge">Job contact</span> }
                </label>
              }
            </div>
          }

          @if (locationContacts().length) {
            <div class="sce-group">
              <div class="sce-group__head">Location contacts</div>
              @for (r of locationContacts(); track r.contactKey) {
                <label class="sce-item" [class.sce-item--disabled]="!r.email">
                  <input
                    type="checkbox"
                    [checked]="isRecipientSelected(r)"
                    [disabled]="!r.email || isSending()"
                    (change)="toggleRecipient(r)" />
                  <span class="sce-item__name">
                    {{ r.name || 'Unnamed contact' }}
                    @if (r.title) { <span class="sce-item__title">— {{ r.title }}</span> }
                  </span>
                  <span class="sce-item__email">{{ r.email || 'No email on file' }}</span>
                  @if (r.isJobDefaultContact) { <span class="sce-item__badge">Job contact</span> }
                </label>
              }
            </div>
          }

          @if (!hasAnyRecipient() && !recipientsError()) {
            <div class="sce-status">No contacts found for this job.</div>
          }

          <!-- Optional message to the customer (V1's editable email note). -->
          <div class="sce-note">
            <label class="sce-note__label" for="sceNote">Note to customer <span class="sce-note__opt">(optional)</span></label>
            <textarea
              id="sceNote"
              class="sce-note__input"
              rows="3"
              [attr.maxlength]="maxNoteLength"
              [value]="customNotes()"
              [disabled]="isSending()"
              placeholder="Add a message that goes out with the estimate"
              (input)="onNoteInput($event)"></textarea>
            <div class="sce-note__count">{{ customNotes().length }} / {{ maxNoteLength }}</div>
          </div>

          <!-- Attachments: nothing is attached unless picked here — the customer reviews the priced
               estimate on the portal via the link in the email. -->
          <div class="sce-attach">
            <button
              type="button"
              class="sce-attach__toggle"
              (click)="toggleAttachmentsPanel()"
              [attr.aria-expanded]="showAttachments()">
              <span class="sce-attach__caret" [class.sce-attach__caret--open]="showAttachments()">▸</span>
              Attach files
              @if (selectedAttachmentCount()) {
                <span class="sce-attach__count">{{ selectedAttachmentCount() }} selected</span>
              } @else if (isLoadingAttachments()) {
                <span class="sce-attach__hint">loading…</span>
              } @else if (!hasAnyAttachment()) {
                <span class="sce-attach__hint">upload or pick files</span>
              }
            </button>

            @if (showAttachments()) {
              <div class="sce-attach__body">
                <div class="sce-attach__note">
                  The customer reviews the estimate on the portal — attach files only if you want to send them alongside it.
                </div>

                <!-- Upload new files (V1's dropzone: "additional file(s) OTHER than the estimate itself"). They go out with
                     the email and are saved on the job's files when it is sent. -->
                <div class="sce-group">
                  <div class="sce-group__head">Upload files</div>
                  <label
                    class="sce-drop"
                    [class.sce-drop--over]="isDragOver()"
                    [class.sce-drop--busy]="isUploading() || isSending()"
                    (dragover)="onDragOver($event)"
                    (dragleave)="onDragLeave($event)"
                    (drop)="onDrop($event)">
                    <input
                      type="file"
                      class="sce-drop__input"
                      multiple
                      [disabled]="isUploading() || isSending()"
                      (change)="onFilesPicked($event)" />
                    <span class="sce-drop__main">
                      @if (isUploading()) { Uploading… } @else { Click to choose files, or drop them here }
                    </span>
                    <span class="sce-drop__hint">
                      Extra files only — up to {{ maxUploadFiles }} files, {{ maxUploadMb }} MB each.
                    </span>
                  </label>

                  @if (uploadError()) {
                    <div class="sce-alert sce-alert--error sce-alert--tight">{{ uploadError() }}</div>
                  }

                  @for (f of stagedFiles(); track f.fileKey) {
                    <div class="sce-item sce-item--static">
                      <span class="sce-item__check" aria-hidden="true">✓</span>
                      <span class="sce-item__name">{{ f.fileName }}</span>
                      <span class="sce-item__email">{{ formatFileSize(f.sizeBytes) }}</span>
                      <button
                        type="button"
                        class="sce-item__remove"
                        [disabled]="isSending()"
                        (click)="removeStagedFile(f)"
                        [attr.aria-label]="'Remove ' + f.fileName">✕</button>
                    </div>
                  }
                </div>

                @if (attachmentsError()) {
                  <div class="sce-alert sce-alert--error">{{ attachmentsError() }}</div>
                }

                @if (isLoadingAttachments()) {
                  <div class="sce-status">Loading files…</div>
                } @else {
                  @if (jobFiles().length) {
                    <div class="sce-group">
                      <div class="sce-group__head">Job files</div>
                      @for (f of jobFiles(); track f.fileKey) {
                        <label class="sce-item">
                          <input
                            type="checkbox"
                            [checked]="isJobFileSelected(f)"
                            [disabled]="isSending()"
                            (change)="toggleJobFile(f)" />
                          <span class="sce-item__name">{{ f.fileName }}</span>
                          @if (f.documentType) { <span class="sce-item__title">— {{ f.documentType }}</span> }
                          @if (f.addedOn) { <span class="sce-item__email">{{ f.addedOn | date: 'M/d/yy' }}</span> }
                          @if (f.fileUrl) {
                            <a
                              class="sce-item__view"
                              [href]="f.fileUrl"
                              target="_blank"
                              rel="noopener"
                              (click)="$event.stopPropagation()"
                              [attr.aria-label]="'Preview ' + f.fileName">View</a>
                          }
                        </label>
                      }
                    </div>
                  }

                  @if (vendorFiles().length) {
                    <div class="sce-group">
                      <div class="sce-group__head">Vendor uploads</div>
                      @for (f of vendorFiles(); track f.fileKey) {
                        <label class="sce-item">
                          <input
                            type="checkbox"
                            [checked]="isVendorFileSelected(f)"
                            [disabled]="isSending()"
                            (change)="toggleVendorFile(f)" />
                          <span class="sce-item__name">{{ f.fileName }}</span>
                          @if (f.vendorName) { <span class="sce-item__title">— {{ f.vendorName }}</span> }
                          @if (f.addedOn) { <span class="sce-item__email">{{ f.addedOn | date: 'M/d/yy' }}</span> }
                          @if (f.fileUrl) {
                            <a
                              class="sce-item__view"
                              [href]="f.fileUrl"
                              target="_blank"
                              rel="noopener"
                              (click)="$event.stopPropagation()"
                              [attr.aria-label]="'Preview ' + f.fileName">View</a>
                          }
                        </label>
                      }
                    </div>
                  }

                  @if (!jobFiles().length && !vendorFiles().length && !attachmentsError()) {
                    <div class="sce-status">No files uploaded to this job yet.</div>
                  }
                }
              </div>
            }
          </div>

          <!-- Per-recipient outcomes, shown after a partial failure. -->
          @if (sendResults().length) {
            <div class="sce-results">
              @for (result of sendResults(); track result.email) {
                <div class="sce-result" [class.sce-result--failed]="!result.sent">
                  {{ result.sent ? '✓' : '✕' }} {{ result.email }}
                  @if (!result.sent && result.message) { — {{ result.message }} }
                </div>
              }
            </div>
          }
        }
      </div>

      <div class="sce-foot">
        <button type="button" class="sce-btn sce-btn--ghost" (click)="cancel()" [disabled]="isSending()">Cancel</button>
        <div class="sce-foot__spacer"></div>
        <button type="button" class="sce-btn sce-btn--send" (click)="send()" [disabled]="!canSend()">
          {{ isSending() ? 'Sending…' : 'Send to ' + selectedContactKeys().size + ' recipient(s)' }}
        </button>
      </div>
    </div>
  </div>
}
